
import Modals from '@/components/shared/modals';
import Nav from '@/components/shared/nav';
import Cookies from '@/components/shared/cookies';
import Logo from '@/assets/icons/logo.svg';
import {
  getAccessToken,
  getUser,
  loginByRedirect,
  loginRedirectCallback,
  logout,
  refreshSession,
  userManager,
  isEmailConfirmedFromToken,
} from '@/utils/behavior/authService';
import { appURLs, inAppRedirect } from '~/utils/route-redirects';
import { AnonymousRedirectURL } from '~/utils/env-vars';
import signalR from '@/plugins/signalr/index';
import { unlocalizeRouteName } from '~/utils/routeNames';
import { mapGetters, mapActions } from 'vuex';
import { AppLoadQueryParams } from '~/utils/behavior/localStorage/localStorageKeys';
import { decodeJWT } from '~/utils/behavior/jwt';
import {
  isNativeIOS,
  isNativePlatform,
  setAndroidNativePlatformType,
  setIOSNativePlatformType,
} from '~/utils/behavior/nativePlatformType';
import { NativePlatformTypes } from '~/types/behavior/nativePlatformType';
import { isMobileResponsive } from '~/utils/mixins';
import {
  getFirstNameFromFullName,
  getLastNameFromFullName,
} from '@/utils/user';
import { eventManager } from '@/utils/behavior/applicationEvents/event-manager';
import { registerPolyfills } from '~/utils/behavior/polyfills';

const contextualizedLogMessage = (message) => `Shared Layout. ${message}`;

// We're setting the login timeout to 60s to allow the app to recover from network issues during Skade deployments.
// During tests on dev the time was closer to 30s but this is to give it leeway for unexpected scenarios.
const _loginTimeoutMillis = 60 * 1000;
const EMAIL_CONFIRMATION_LOOP_DELAY = 2000;

export default {
  name: 'SharedLayout',
  components: { Modals, Nav, Cookies, Logo },
  mixins: [isMobileResponsive],
  props: {
    showNav: {
      type: Boolean,
      required: false,
      default: true,
    },
    showModals: {
      type: Boolean,
      required: false,
      default: true,
    },
    showCookies: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      /**
       * The timeout for the login process.
       * If the login process does not complete within this time, the user is redirected to the login page.
       */
      loginTimeout: null,
      /**
       * The instance returned by setTimoout for the email confirmation loop.
       * This will be null if no timeout loop is currently running.
       */
      emailConfirmationLoopTimeout: null,
    };
  },
  computed: {
    ...mapGetters('layouts', ['loggedIn']),
    ...mapGetters('authentication', ['me', 'accountProvider', 'oidcUsername']),
    ...mapGetters('notifications', ['notificationCount']),
    // Pilots whereas the layout should have a column for widgets
    showWidgets() {
      if (this.isMobileResponsive) return false;

      // TODO use route-redirects or roteNames here
      const noWidgetRoutes = [
        'academy',
        'crypto',
        'markets',
        'leagues',
        'messages',
        'portfolio',
        'settings',
        'notifications-center',
      ];
      return !noWidgetRoutes.some((r) => this.$route.name?.includes(r));
    },
  },
  watch: {
    notificationCount: function (newValue, oldValue) {
      console.debug(contextualizedLogMessage('Notification count watcher'), {
        newValue,
        oldValue,
      });

      if (newValue < 0) {
        console.error(
          contextualizedLogMessage(
            'Notification count is negative. This is unexpected behavior - setting it to zero instead.'
          )
        );
        newValue = 0;
      }

      const exactSame = newValue === oldValue;
      const shouldSetZero = newValue === 0;
      const shouldSetNonZero = !!newValue;
      const shouldUpdate = !exactSame && (shouldSetZero || shouldSetNonZero);

      if (!shouldUpdate) {
        console.debug(
          contextualizedLogMessage('No need to update iOS notification badge')
        );
        return;
      }

      try {
        // If we're running on the native iOS platform, update the native app badge count
        if (isNativeIOS()) {
          console.debug(
            contextualizedLogMessage(
              'Notification count changed. Preparing to update iOS badge'
            )
          );

          const updateBadgeFunction = window.skade?.sdk?.setBadgeNumber;

          if (!updateBadgeFunction) {
            console.error(
              contextualizedLogMessage(
                'No setBadgeNumber function found on window.skade.sdk'
              )
            );
            return;
          }

          if (typeof updateBadgeFunction !== 'function') {
            console.error(
              contextualizedLogMessage(
                'setBadgeNumber on window.skade.sdk is not a function'
              )
            );
            return;
          }

          updateBadgeFunction(Math.min(1, newValue));

          console.debug(
            contextualizedLogMessage(
              'iOS notification badge updated successfully'
            )
          );
        }
      } catch (error) {
        console.error(
          contextualizedLogMessage(
            'Failed to update iOS notification badge',
            error
          )
        );
      }
    },
  },
  async mounted() {
    console.debug(contextualizedLogMessage('Mounted'));

    registerPolyfills();

    this.setLoginTimeout();

    // TODO add special treatment in case the user is a web crawler

    const {
      shouldRefreshAccessToken,
      code,
      state,
      scope,
      session_state,
      axeptio_token: axeptioToken,
      mobileApp,
    } = this.$route.query;

    const redirectToLogin = this.$route.query.redirectToLogin === 'true';

    console.debug(contextualizedLogMessage(`Query param state`), {
      shouldRefreshAccessToken,
      code,
      scope,
      state,
      session_state,
      axeptioToken,
      mobileApp,
    });

    let loginRedirectCallbackSuccess = false;
    let userAvailable = false;
    let user = undefined;
    let newlyRegistered = false;

    // Save the native platform type into app storage to ensure the information is not lost during the session.
    this.saveNativePlatformType({ mobileApp });

    // TODO extract into function to simplify the main flow

    // save existing route or query parameters, so that we can redirect the user back to where they were
    this.saveQueryParams({ axeptioToken });

    // only call the login redirect callback if there are expected URL query parameters
    if (code && state && scope) {
      console.debug(
        contextualizedLogMessage('Starting login redirect callback')
      );

      try {
        user = await loginRedirectCallback();

        console.debug(
          contextualizedLogMessage(
            'Login redirect callback finished. Obtained user'
          ),
          user
        );

        userAvailable = true;
        loginRedirectCallbackSuccess = true;
      } catch (ex) {
        console.error(
          contextualizedLogMessage('Failed to execute login redirect callback'),
          ex
        );

        userAvailable = false;
        loginRedirectCallbackSuccess = false;
      }
    } else
      console.debug(
        contextualizedLogMessage(
          'One of `code`, `state` or `scope` is not available. Skipping login redirect callback'
        )
      );

    // TODO extract into function to simplify the main flow

    try {
      console.debug(contextualizedLogMessage('Getting user.'));
      user = await getUser();

      if (!user) {
        console.debug(contextualizedLogMessage('No user found.'));
        userAvailable = false;
      } else {
        console.debug(contextualizedLogMessage('Found user.', user));
        userAvailable = true;
      }
    } catch (ex) {
      console.error(contextualizedLogMessage('Failed to get user'));

      userAvailable = false;
    }

    console.debug(contextualizedLogMessage('Login state checkpoint'), {
      loginRedirectCallbackSuccess,
      userAvailable,
    });

    // TODO extract into function to simplify the main flow

    if (!userAvailable) {
      console.debug(
        contextualizedLogMessage(
          'User is unavailable. Deciding the next redirect'
        )
      );

      // In case the query parameter indicates the app is running in a mobile app, that means this is a fresh launch of the app.
      // As such, we should redirect unauthenticated users to the mobile welcome page.
      if (mobileApp) {
        console.debug(
          contextualizedLogMessage(
            'Mobile device query parameter detected. Redirecting to welcome page'
          )
        );
        await this.redirectToWelcome();
        return;
      }

      // In case there is a is a `redirectToLogin` query parameter, and it's true, redirect the user to login
      if (redirectToLogin) {
        console.debug(
          contextualizedLogMessage(
            'redirectToLogin query parameter detected. Redirecting to login'
          )
        );
        await loginByRedirect();
        return;
      }

      // If the app is running on a native platform, we must avoid redirecting to webflow.
      // Instead, we redirect users to the mobile app welcome page.
      // See ticket https://waleo-product.atlassian.net/browse/SKD-1828 for the discussion and decisions.
      if (isNativePlatform()) {
        console.debug(
          contextualizedLogMessage(
            'Native platform detected. Redirecting to welcome page'
          )
        );
        await this.redirectToWelcome();
        return;
      }

      // This handles the case where user is not logged in.
      console.debug(
        contextualizedLogMessage(
          `Redirecting to ${AnonymousRedirectURL} by default`
        )
      );
      window.location.href = AnonymousRedirectURL;
      return;
    }

    if (user.expired) {
      console.debug(
        contextualizedLogMessage(
          'User session has expired. Attempting token refresh'
        )
      );

      user = await refreshSession();

      console.debug(
        contextualizedLogMessage('Successfully refreshed user token')
      );

      userAvailable = true;
    }

    console.debug(contextualizedLogMessage('Login state checkpoint'), {
      loginRedirectCallbackSuccess,
      userAvailable,
    });

    console.debug(
      contextualizedLogMessage('User is available. Login finished')
    );

    // refresh access token based on query param
    if (shouldRefreshAccessToken === 'true') {
      console.debug(
        contextualizedLogMessage(
          'Refreshing access token due to the query parameter `shouldRefreshAccessToken`'
        )
      );

      await refreshSession();

      console.debug(
        contextualizedLogMessage(
          'Finished refreshing access token due to the query parameter `shouldRefreshAccessToken`'
        )
      );
    }

    await this.updateDecodedToken();

    try {
      newlyRegistered = await this.enrichStoreWithAuth();

      if (code && state && scope) {
        // Raising GTM login after getting profile information, only if we're in a login scenario
        eventManager.raiseLoginEvent({
          user_email: this.me?.email,
          user_first_name: this.me?.username,
          user_id: this.me?.userId,
          login_method: this.accountProvider,
        });
      }
    } catch (ex) {
      console.error(
        contextualizedLogMessage(
          'Hard failure when enriching store with auth. Logging out the user'
        ),
        ex
      );
      // With network error handling and retries, this is a hard failure
      // We could picture keeping the user in the app to display him more information, eventually leave him a way to contact us
      // But this is most likely not recoverable
      await logout();
    }

    this.clearLoginTimeout();

    this.updateQueryParams();

    this.registerSignalR();

    this.raiseGtmInitialEvents();

    if (this.handleOnboardingRedirect(newlyRegistered, axeptioToken)) {
      console.debug(
        contextualizedLogMessage(
          'Since the user should be redirected to onboarding, skip setting the logged in state in this layout instance'
        )
      );

      return;
    }

    console.debug(
      contextualizedLogMessage(
        'User is considered logged in. Allowing user to see the page'
      )
    );

    this.setLoggedIn(true);

    await this.setupVisibilityHandlers();

    await this.setupSWHandlers();

    // If we're on the native iOS platform, notify the native app that the web app is loaded
    if (isNativeIOS()) {
      try {
        const onAppLoadedHandler = window.skade?.sdk?.onAppLoaded;
        if (onAppLoadedHandler) {
          console.debug(
            contextualizedLogMessage(
              'Native iOS platform detected. Calling onAppLoadedHandler'
            )
          );
          onAppLoadedHandler();
          console.debug(
            contextualizedLogMessage('Called onAppLoadedHandler successfully')
          );
        } else {
          // If the native platform is iOS and the handler isn't available, this is an error and needs to be logged
          console.error(
            contextualizedLogMessage(
              'No onAppLoadedHandler found on window.skade.sdk'
            )
          );
        }
      } catch (ex) {
        console.error(
          contextualizedLogMessage('Error on calling onAppLoadedHandler')
        );
      }
    }
  },
  beforeDestroy() {
    this.clearLoginTimeout();
    this.removeVisibilityHandlers();
  },
  methods: {
    ...mapActions('layouts', ['setLoggedIn']),
    ...mapActions('authentication', ['getMe']),
    ...mapActions('users', ['updateUsersMe']),
    ...mapActions('profile', ['createProfile']),
    /**
     * Saves the native platform type into the app storage.
     * This is used to determine whether the app is running on a mobile device or not.
     *
     * Treat this as if it is very sensitive, because it determines some key behaviors of the app, like redirection and features that are platform-specific.
     *
     * It is important that the storage used for the native platform type is appropriate for the job.
     * Local storage is not appropriate because it's state is shared between opening the native app and the app in the browser on Android.
     * Session storage is a good candidate because it relates to the currently open tab.
     */
    saveNativePlatformType({ mobileApp }) {
      try {
        if (!mobileApp) {
          console.debug(
            contextualizedLogMessage('No mobile app query parameter detected.')
          );
          return;
        }

        if (mobileApp === NativePlatformTypes.Android) {
          console.debug(
            contextualizedLogMessage('Setting native platform type to Android')
          );

          setAndroidNativePlatformType();
        } else if (mobileApp === NativePlatformTypes.IOS) {
          console.debug(
            contextualizedLogMessage('Setting native platform type to iOS')
          );
          setIOSNativePlatformType();
        } else {
          console.error(
            contextualizedLogMessage(
              `Unknown mobile app type ${mobileApp}. Not setting native platform type`
            )
          );
        }
      } catch (e) {
        console.error(
          contextualizedLogMessage(
            'Failed to save native platform type. This is unexpected'
          ),
          e
        );
      }
    },
    getAppLoadQueryParams() {
      try {
        return JSON.parse(localStorage.getItem(AppLoadQueryParams) ?? '{}');
      } catch (e) {
        console.error(
          contextualizedLogMessage(
            'Failed to parse saved app load query params. Returning empty object'
          )
        );
        return {};
      }
    },
    setAppLoadQueryParams(params) {
      localStorage.setItem(AppLoadQueryParams, JSON.stringify(params));
    },
    clearAppLoadQueryParams() {
      localStorage.setItem(AppLoadQueryParams, JSON.stringify({}));
    },
    // TODO document this more extensively and maybe rename it
    saveQueryParams({ axeptioToken }) {
      try {
        // fetch already saved query params
        const alreadySavedParams = this.getAppLoadQueryParams();

        // specify more query parameters that should be saved here
        const paramsToSave = {
          axeptio_token: axeptioToken,
          ...alreadySavedParams,
        };

        console.debug(
          contextualizedLogMessage('Saving query params'),
          paramsToSave
        );

        this.setAppLoadQueryParams(paramsToSave);
      } catch (ex) {
        console.error(
          contextualizedLogMessage(`Failed to save query parameters`),
          ex
        );
      }
    },
    /**
     * Sets the login timeout timer.
     * The timer redirects the user back to login in case login does not succeed within the duration of the timer.
     * This timer should be cleared if login succeeds.
     *
     * @function setLoginTimeout
     * @returns {void}
     */
    setLoginTimeout() {
      console.debug(contextualizedLogMessage('Setting login timeout.'));

      // In case login isnt complete or determined within `_loginTimeoutMillis` milliseconds, redirect to login
      this.loginTimeout = window.setTimeout(async () => {
        console.error(contextualizedLogMessage('Login timeout reached.'));

        const user = await getUser();

        const storeMe = this.$store.getters['authentication/me'];

        // TODO decide on whether to logout regardless or not, as it impacts the overall behavior
        if (!user) {
          console.error(
            contextualizedLogMessage(
              'Login timeout found no user. Redirecting to login'
            )
          );

          await loginByRedirect();
        } // if the user's session has expired despite waiting, logout the user
        else if (user.expired) {
          console.error(
            contextualizedLogMessage('User session expired. Logging out'),
            user
          );

          await logout();
        } // if theres no profile present, logout the user
        else if ((storeMe?.profiles?.length ?? 0) === 0) {
          console.error(
            contextualizedLogMessage('No profile found for user. Logging out')
          );

          await logout();
        } else {
          console.info(
            contextualizedLogMessage(
              'Login timeout found a valid user. Exiting timeout'
            )
          );
        }
      }, _loginTimeoutMillis);
    },
    /**
     * Updates the store information with the decoded access token, if available.
     * Returns otherwise.
     *
     * @function updateDecodedToken
     * @returns {Promise<void>}
     */
    async updateDecodedToken() {
      const user = await userManager.getUser();

      const token = user?.access_token;

      if (!token) {
        console.error(
          contextualizedLogMessage(
            'Cannot update decoded token, as the token is unavailable. This is unexpected behavior'
          )
        );
        return;
      }

      const decodedToken = decodeJWT(token);

      console.debug(contextualizedLogMessage('Setting decoded token'), {
        token,
        decodedToken,
      });

      this.$store.dispatch('authentication/setDecodedToken', decodedToken);
    },
    /**
     * Check if `this.loginTimeout` is set and clear that timeout if so, while setting `this.loginTimeout` to `null`.
     * Immediately returns otherwise.
     *
     * @function clearLoginTimeout
     * @returns {void}
     */
    clearLoginTimeout() {
      if (!this.loginTimeout) {
        console.debug(
          contextualizedLogMessage('No login timeout was set. Returning')
        );
        return;
      }

      console.debug(contextualizedLogMessage('Clearing login timeout'));
      window.clearTimeout(this.loginTimeout);
      this.loginTimeout = null;
    },
    /**
     * Asynchronously checks if the user is registered and updates the store with the user's authentication and profile information.
     * If the user is not registered, it creates a new user and profile in the API.
     *
     * @async
     * @function enrichStoreWithAuth
     * @returns {Promise<boolean>} Returns a promise that resolves to a boolean indicating whether a new user was registered.
     */
    async enrichStoreWithAuth() {
      // In here, network errors are handled on a higher level, and hard failure handling is delegated to caller
      // Only case we need to handle is the expected 404 error in case a new user is detected
      let newlyRegistered = false;

      try {
        await this.getMe({
          fetchQuickstartProgress: true,
          fetchPortfolioCount: true,
        });
      } catch (err) {
        if (err.response && err.response?.status === 404 && !this.me) {
          // New account, need to create the user and the profile in the API
          console.debug(
            contextualizedLogMessage(
              'User not found. A newly registered user should now be logged in. Creating the api user and profile'
            )
          );
          newlyRegistered = true;
          const username = this.oidcUsername;
          const tokenEmailConfirmed = await isEmailConfirmedFromToken();

          await this.updateUsersMe({
            username: username,
          });

          await this.createProfile({
            profileName: username,
            displayName: username,
            preferredLocale: this.$i18n.locale,
            isEmailConfirmed: tokenEmailConfirmed,
          });
          // TODO - API should be returning the profile creation result, and it should be handled and set to the store without the need of an extra call
          await this.getMe({
            fetchQuickstartProgress: true,
            fetchPortfolioCount: true,
          }).then(() => {
            // Raising GTM register event
            eventManager.raiseRegisterEvent({
              user_email: this.me?.email,
              user_first_name: this.me?.username,
              user_id: this.me?.userId,
              signup_method: this.accountProvider,
            });
          });
        } else {
          // Unhandled error when trying to get profile info;
          // Logging and rethrowing
          console.error(
            contextualizedLogMessage(
              'Unknown error when calling api/getMe. Assuming it is not transient',
              err
            )
          );
          throw err;
        }
      }
      if (this.me && this.me.profiles) {
        console.debug(
          contextualizedLogMessage(
            'User creation and profile setup complete. Proceeding.'
          )
        );
        await this.$store.dispatch('authentication/getUserMe');
        await this.$store.dispatch('authentication/setProfileId');

        console.debug(
          contextualizedLogMessage(
            'Finished enriching store with auth, user successfully found'
          )
        );

        return newlyRegistered;
      } else {
        // We're now throwing an error from the store logic if api response was 200 with no user or profile
        // So this should have bubbled up sooner
        console.error(
          contextualizedLogMessage('enrichStoreWithAuth. Unexpected empty user')
        );
        throw new Error('enrichStoreWithAuth failed');
      }
    },
    /**
     * Removes, i.e. unsets the query parameters used by the OIDC callback.
     * @returns {void}
     */
    updateQueryParams() {
      console.debug(contextualizedLogMessage('Updating query params'));

      const savedParams = this.getAppLoadQueryParams();

      console.debug(
        contextualizedLogMessage(
          'Restoring saved params and overriding with route query params'
        ),
        savedParams
      );

      const queryParamsToSet = { ...savedParams, ...this.$route.query };

      if (Object.keys(queryParamsToSet).length === 0) {
        console.debug(
          contextualizedLogMessage(
            'There are no query parameters set. Not clearing or setting any query parameters'
          )
        );

        return;
      }

      delete queryParamsToSet.code;
      delete queryParamsToSet.state;
      delete queryParamsToSet.scope;
      delete queryParamsToSet.session_state;
      delete queryParamsToSet.redirectToLogin;
      delete queryParamsToSet.shouldRefreshAccessToken;
      delete queryParamsToSet.mobileApp;

      this.$router.replace({ query: queryParamsToSet });

      console.debug(
        contextualizedLogMessage(
          'Restored query params and cleared OIDC query params'
        ),
        queryParamsToSet
      );

      this.clearAppLoadQueryParams();
    },
    /**
     * Initialize the SignalR hubs.
     */
    registerSignalR() {
      console.debug(contextualizedLogMessage('Registering SignalR'));

      signalR(this.$nuxt.context);
    },
    /**
     * If the user is newly registered or hasn't completed the onboarding steps, redirect them to onboarding.
     * Returns whether the user should be redirected to onboarding.
     *
     * @function handleOnboardingRedirect
     * @param {Boolean} newlyRegistered Whether the user has just registered
     * @param {string} axeptioToken The axeptio token to set, if available
     * @returns {Boolean} Whether the user should be redirected to onboarding
     */
    handleOnboardingRedirect(newlyRegistered, axeptioToken) {
      // if already on onboarding, return false
      if (unlocalizeRouteName(this.$route) === appURLs.onboarding()) {
        console.debug(
          contextualizedLogMessage(
            'The user is already on onboarding. They should not be redirected to onboarding again'
          )
        );

        return false;
      }

      const hasCompletedOnboarding =
        this.$store.getters['onboarding/hasCompletedTutorial'] === true;

      const shouldRedirectToOnboarding =
        newlyRegistered || !hasCompletedOnboarding;

      console.debug(contextualizedLogMessage(`Onboarding completion status`), {
        newlyRegistered,
        hasCompletedOnboarding,
        shouldRedirectToOnboarding,
      });

      if (shouldRedirectToOnboarding) {
        console.debug(contextualizedLogMessage('Redirecting to onboarding.'));

        inAppRedirect(
          this.localePath({
            name: appURLs.onboarding(),
            query: {
              axeptio_token: axeptioToken,
            },
          })
        );
      }

      return shouldRedirectToOnboarding;
    },
    // TODO extract into separate file
    /**
     * Register event handlers for service-worker related functionalities.
     * @returns {void}
     */
    async setupSWHandlers() {
      try {
        console.debug(
          contextualizedLogMessage('Setting up service worker message handlers')
        );

        const wb = await window.$workbox;

        // TODO wait until service worker becomes available
        // TODO/NOTE - i've found inconsistent behavior between chrome and firefox when it came down to registering service workers
        // it seemed like on firefox the service worker wouldnt register sometimes
        if (!window.navigator.serviceWorker) {
          console.error(
            contextualizedLogMessage(
              'window.navigator.serviceWorker is unavailable. This is unexpected at this point.'
            )
          );

          return;
        }

        console.debug(
          contextualizedLogMessage('Registering serviceWorker message handler'),
          wb
        );

        window.navigator.serviceWorker.addEventListener(
          'message',
          async (event) => {
            console.debug(
              contextualizedLogMessage('Received service worker message'),
              event
            );

            const swEvent = event.data;

            if (!swEvent.type) {
              console.error(
                contextualizedLogMessage(
                  'No message type on service worker message. This is unexpected'
                )
              );

              return;
            }

            // this type should be carefully coordinated with skade-sw.js
            switch (swEvent.type) {
              default:
                console.error(
                  contextualizedLogMessage(
                    `Unhandled service worker message type ${swEvent.type}`
                  )
                );
            }
          }
        );
      } catch (ex) {
        console.error(
          contextualizedLogMessage('Failed to setup service worker handlers'),
          ex
        );
      }
    },
    // TODO extract this into a mixin
    /**
     * Checks if the document is hidden or not.
     */
    documentHidden() {
      return document.visibilityState === 'hidden';
    },
    /**
     * Checks if the document is visible or not.
     */
    documentVisible() {
      return document.visibilityState === 'visible';
    },
    /**
     * Sets up window/document/browser tab visibility handlers, and starts the email confirmation loop.
     * These visibility handlers are used for dealing with email confirmation checks and the necessary token refreshing that should follow.
     *
     * If a browser tab is visible, there should be a periodically executing loop in which we check whether the user's email confirmation status matches that of the token.
     * If they do not match and its the token that is inconsistent, we should refresh the token.
     * If the tab is no longer visible, the loop for that tab should stop and restart when the tab gains focus.
     * Whenever we perform a check, we re-establish the email confirmation status in the auth provider and in the token.
     */
    async setupVisibilityHandlers() {
      console.debug(contextualizedLogMessage('Setting visibility handlers'));

      // visibility event handlers
      document.addEventListener(
        'visibilitychange',
        this.emailConfirmationVisibilityChangeHandler
      );

      // start the loop once the app starts
      this.startEmailConfirmationLoop();
    },
    /**
     * Removes the visibility handlers that were set up in `setupVisibilityHandlers` and stops the email confirmation loop.
     * This should be called when the layout is destroyed.
     */
    async removeVisibilityHandlers() {
      console.debug(contextualizedLogMessage('Removing visibility handlers'));

      // remove the visibility change handler
      document.removeEventListener(
        'visibilitychange',
        this.emailConfirmationVisibilityChangeHandler
      );

      // stop the email confirmation loop
      this.stopEmailConfirmationLoop();
    },
    /**
     * Handles the visibility change event for email confirmation checks.
     * If the document is hidden, it stops the email confirmation loop.
     * If the document is visible, it starts the email confirmation loop.
     */
    emailConfirmationVisibilityChangeHandler() {
      if (this.documentHidden()) {
        console.debug(
          contextualizedLogMessage('Visibility changed. Document hidden')
        );
        this.stopEmailConfirmationLoop();
      } else if (this.documentVisible()) {
        console.debug(
          contextualizedLogMessage('Visibility changed. Document visible')
        );
        this.startEmailConfirmationLoop();
      } else
        console.error(
          contextualizedLogMessage('Unknown document visibility state'),
          {
            state: document.visibilityState,
          }
        );
    },
    /**
     * Email confirmation loop that periodically checks the email confirmation status of the user.
     */
    async emailConfirmationLoop() {
      try {
        // if the tab isnt visible, stop the loop
        if (this.documentHidden()) {
          console.debug(
            contextualizedLogMessage(
              'Tab is hidden. Stopping email confirmation loop'
            )
          );
          this.stopEmailConfirmationLoop();
          return;
        }

        // establish the auth provider email confirmation status
        await this.$store.dispatch('authentication/getUserMe');

        const authProviderEmailConfirmed = await this.$store.getters[
          'authentication/isEmailConfirmed'
        ];

        // establish the token email confirmation status
        const token = await getAccessToken();
        const tokenEmailConfirmed = decodeJWT(token).ec === true;

        // check for mismatch
        const noMistmatch = authProviderEmailConfirmed === tokenEmailConfirmed;

        console.debug(contextualizedLogMessage('Email confirmation status'), {
          authProviderEmailConfirmed,
          tokenEmailConfirmed,
          noMistmatch,
        });

        // handle each case accordingly

        // If there's no mismatch and both are true, then the email is confirmed and the access token is up to date, so stop the loop and remove the visibility handlers.
        if (noMistmatch && tokenEmailConfirmed) {
          console.debug(
            contextualizedLogMessage(
              'No mismatch and token email is confirmed. Stopping email confirmation loop and removing visibility handlers'
            )
          );

          // Removing the visibility handlers also stops the loop.
          this.removeVisibilityHandlers();
        }
        // If there is a mismatch and it's only the token that isn't up to date with the auth provider, initiate a refresh and stop the loop if it succeeds and remove the visibility handlers.
        else if (!noMistmatch && !tokenEmailConfirmed) {
          console.debug(
            contextualizedLogMessage(
              "There is a mismatch and the access token's ec claim is false. Initiating refresh"
            )
          );
          await refreshSession();
          console.debug(
            contextualizedLogMessage(
              'Refreshed access token. Email confirmation status should be consolidated now. Stopping email confirmation loop and removing visibility handlers.'
            )
          );
          // Removing the visibility handlers also stops the loop.
          this.removeVisibilityHandlers();
        }
        // do not handle other cases as we can't do anything about them
        else {
          console.debug(
            contextualizedLogMessage(
              'No actions to take in email confirmation loop'
            )
          );
        }
      } finally {
        // Schedule the next run only if the loop is still active
        if (this.emailConfirmationLoopTimeout) {
          console.debug(
            contextualizedLogMessage(
              `Scheduling next email confirmation loop run in ${EMAIL_CONFIRMATION_LOOP_DELAY}ms`
            )
          );
          this.startEmailConfirmationLoop(EMAIL_CONFIRMATION_LOOP_DELAY);
        }
      }
    },
    /**
     * Stops the email confirmation loop if it is running.
     * Clears the timeout if it exists.
     */
    stopEmailConfirmationLoop() {
      console.debug(
        contextualizedLogMessage('Stopping email confirmation loop')
      );

      if (this.emailConfirmationLoopTimeout) {
        clearTimeout(this.emailConfirmationLoopTimeout);
        this.emailConfirmationLoopTimeout = null;
        console.debug(
          contextualizedLogMessage('Clearing email confirmation timeout')
        );
      } else {
        console.debug(
          contextualizedLogMessage('No email confirmation timeout to clear')
        );
      }
    },
    /**
     * Starts the email confirmation loop.
     *
     * If the loop is already running, it will stop it first and then start it again.
     * As such, calling this repeatedly should not create multiple loops, but it may delay the next execution of the loop.
     */
    startEmailConfirmationLoop(delay = 0) {
      console.debug(
        contextualizedLogMessage('Starting email confirmation loop')
      );

      // Double check to ensure we aren't running two timeouts
      if (this.emailConfirmationLoopTimeout) this.stopEmailConfirmationLoop();

      this.emailConfirmationLoopTimeout = setTimeout(
        this.emailConfirmationLoop,
        delay
      );
    },
    // Redirects the user to the auth provider welcome page
    redirectToWelcome() {
      window.location.href = `${process.env.NUXT_ENV_API_OIDC}/welcome/index`;
    },
    raiseGtmInitialEvents() {
      try {
        this.raiseGtmPageViewEvent();
        this.raiseGtmUserIdentifyEvent();
      } catch (e) {
        console.error(
          contextualizedLogMessage('Failed to raise GTM initial events', e)
        );
      }
    },
    raiseGtmPageViewEvent() {
      // Track initial page view since router.afterEach won't fire for the first page
      eventManager.raisePageViewEvent(this.$route);
    },

    raiseGtmUserIdentifyEvent() {
      // Set up user_identify GTM event
      const userData = {
        userId: this.me?.userId,
        accountProvider: this.accountProvider,
        email: this.me?.email,
        firstName: getFirstNameFromFullName(this.me?.name),
        lastName: getLastNameFromFullName(this.me?.name),
        name: this.me?.name,
        createdDateUtc: this.me?.createdDateUtc,
      };

      eventManager.raiseUserIdentifyEvent(userData);
    },
  },
};
